import { Button, Icon, Tooltip } from '@blueprintjs/core';
import {
  StatusSelect,
  StatusSelectButton,
} from 'Components/Generic/BOSSelect/StatusSelect';
import FloatingActionMenu from 'Components/Generic/Buttons/FloatingActionMenu';
import AppData from 'Data/AppData';
import RequestData from 'Data/RequestData';
import ConditionalWrapper from 'Lib/hoc/ConditionalWrapper';

import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { getStatusObject, REQUEST_STATUS, REQUEST_STATUS_KEYS } from '../Enum';
import styles from './RequestStatus.module.css';

const RequestStatusButton = ({
  request = {},
  onStatusButtonClicked = () => {},
  disabled = false,
  fillWidth = false,
}) => {
  let disableButton =
    disabled ||
    (request.status === REQUEST_STATUS_KEYS.NEW &&
      !request.line_items.length) ||
    (request.status === REQUEST_STATUS_KEYS.APPROVED &&
      !!request.processing_user_id &&
      request.processing_user_id !== AppData.userID());
  let tooltipText =
    request.status === REQUEST_STATUS_KEYS.NEW
      ? 'Requests must have one or more line items'
      : 'You are not the assigned processor.';

  const getActionButton = (statusObject, clickAction, buttonLabel) => {
    return (
      <ConditionalWrapper
        wrapper={(children) => (
          <Tooltip content={tooltipText}>{children}</Tooltip>
        )}
        condition={disableButton}>
        <Button
          className={
            disableButton
              ? styles.disabled_button
              : styles.request_status_button
          }
          large
          style={
            statusObject?.color && {
              backgroundColor: statusObject?.color,
            }
          }
          disabled={disableButton}
          onClick={clickAction}>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
            {statusObject?.icon && (
              <Icon
                className={styles.request_status_button_icon}
                icon={statusObject?.icon}
              />
            )}
            {buttonLabel || statusObject?.next_step_label || statusObject.label}
          </div>
        </Button>
      </ConditionalWrapper>
    );
  };

  let statusObject = getStatusObject(request.status);

  if (
    request.transactions?.length &&
    request.status < REQUEST_STATUS_KEYS.TRANSACTION_CREATED
  ) {
    statusObject = REQUEST_STATUS[REQUEST_STATUS_KEYS.TRANSACTION_CREATED];
  } else if (
    !request.transactions?.length &&
    request.status > REQUEST_STATUS_KEYS.SUBMITTED
  ) {
    statusObject = REQUEST_STATUS['2_APPROVED'];
  }

  let buttonComponent = (
    <FloatingActionMenu>
      <FloatingActionMenu.MainButton>
        {getActionButton(statusObject, () =>
          onStatusButtonClicked(statusObject.key),
        )}
      </FloatingActionMenu.MainButton>
    </FloatingActionMenu>
  );

  if (statusObject === REQUEST_STATUS[REQUEST_STATUS_KEYS.SUBMITTED]) {
    buttonComponent = (
      <FloatingActionMenu fillWidth={fillWidth}>
        <FloatingActionMenu.MainButton>
          {getActionButton(statusObject)}
        </FloatingActionMenu.MainButton>
        <FloatingActionMenu.SubMenuButtons>
          {getActionButton(
            statusObject,
            () => onStatusButtonClicked(statusObject.key, { approved: true }),
            `${statusObject.next_step_label}`,
          )}
          {getActionButton(
            { color: 'var(--status_rejected)', icon: 'disable' },
            () => onStatusButtonClicked(statusObject.key, { approved: false }),
            `Reject Order`,
          )}
        </FloatingActionMenu.SubMenuButtons>
      </FloatingActionMenu>
    );
  }

  if (statusObject === REQUEST_STATUS[REQUEST_STATUS_KEYS.APPROVED]) {
    buttonComponent = !request.processing_user_id ? (
      <FloatingActionMenu>
        <FloatingActionMenu.MainButton>
          {getActionButton(
            { icon: 'confirm', color: 'var(--bp-red3)' },
            () => RequestData.claim(request),
            `Claim Order`,
          )}
        </FloatingActionMenu.MainButton>
      </FloatingActionMenu>
    ) : (
      <FloatingActionMenu>
        <FloatingActionMenu.MainButton>
          {getActionButton(
            statusObject,
            request.line_items.filter((li) => !li.assigned_transaction_id)
              .length === 1
              ? () => onStatusButtonClicked(statusObject.key)
              : undefined,
          )}
        </FloatingActionMenu.MainButton>
        {request.line_items.filter((li) => !li.assigned_transaction_id).length >
          1 && (
          <FloatingActionMenu.SubMenuButtons>
            {getActionButton(
              { icon: 'fork', color: 'var(--cp3-purple)' },
              () => onStatusButtonClicked(statusObject.key, { split: true }),
              `Split into multiple transactions`,
            )}
            {getActionButton(statusObject, () =>
              onStatusButtonClicked(statusObject.key),
            )}
          </FloatingActionMenu.SubMenuButtons>
        )}
      </FloatingActionMenu>
    );
  }

  return buttonComponent;
};

RequestStatusButton.propTypes = {
  request: PropTypes.object,
  onStatusButtonClicked: PropTypes.func,
  disabled: PropTypes.bool,
  fillWidth: PropTypes.bool,
};

const RequestStatusLabel = ({
  selectedStatus,
  onSelectStatus,
  placeholder = 'Select a status',
  disabled = false,
  small = false,
}) => {
  const [selectedStatusObject, setSelectedStatusObject] =
    useState(selectedStatus);

  useEffect(() => {
    selectedStatus && setSelectedStatusObject(selectedStatus);
  }, [selectedStatus]);

  return disabled ? (
    selectedStatusObject ? (
      <StatusSelectButton
        statusObject={selectedStatusObject}
        disabled={disabled}
        small={small}
      />
    ) : (
      <Button intent={'primary'}>{placeholder}</Button>
    )
  ) : selectedStatusObject === REQUEST_STATUS['1_SUBMITTED'] ? (
    <RequestStatusButton />
  ) : (
    <StatusSelect
      selectedStatusObject={selectedStatusObject}
      onSelectStatus={onSelectStatus}
      statusObjectList={Object.values(REQUEST_STATUS).filter(
        (status) => status.key !== REQUEST_STATUS_KEYS.NEW,
      )}
      small={small}
    />
  );
};

RequestStatusLabel.propTypes = {
  selectedStatus: PropTypes.object.isRequired,
  onSelectStatus: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  small: PropTypes.bool,
};

export { RequestStatusButton, RequestStatusLabel };
